import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

const makeUid = () => Math.random().toString(36).slice(2, 9);

const ProfileForm = ({ profile, targetUserId = null, onSaved, showFileInput = true, onClose }) => {
  // structure state for dynamic lists (only structure changes cause re-render)
  const [accounts, setAccounts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [otherInfo, setOtherInfo] = useState([]);

  // refs hold live values for inputs (typing won't cause re-renders)
  const fieldsRef = useRef({
    name: '',
    email: '',
    tin: '',
    mobile: '',
    registered_mobile: '',
    profession: '',
    dob: '',
    spouse_tin: '',
    nid_no: '',
    blood_group: '',
    father_name: '',
    mother_name: '',
    spouse_name: '',
    address: '',
    other_info_note: '',
    file: null
  });

  // per-item refs for lists: { uid: {fieldName: value, ...}, ... }
  const accountsRef = useRef({});
  const assetsRef = useRef({});
  const liabilitiesRef = useRef({});
  const otherRef = useRef({});

  // initialize when profile.id changes (keeps refs stable while typing)
  useEffect(() => {
    if (!profile) {
      // reset
      setAccounts([]);
      setAssets([]);
      setLiabilities([]);
      setOtherInfo([]);
      fieldsRef.current = {
        ...fieldsRef.current,
        name: '',
        email: '',
        tin: '',
        mobile: '',
        registered_mobile: '',
        profession: '',
        dob: '',
        spouse_tin: '',
        nid_no: '',
        blood_group: '',
        father_name: '',
        mother_name: '',
        spouse_name: '',
        address: '',
        other_info_note: '',
        file: null
      };
      accountsRef.current = {};
      assetsRef.current = {};
      liabilitiesRef.current = {};
      otherRef.current = {};
      return;
    }

    // top-level fields
    fieldsRef.current.name = profile.name || profile.full_name || '';
    fieldsRef.current.email = profile.email || '';
    fieldsRef.current.tin = profile.tin || '';
    fieldsRef.current.mobile = profile.mobile || '';
    fieldsRef.current.registered_mobile = profile.registered_mobile || '';
    fieldsRef.current.profession = profile.profession || '';
    fieldsRef.current.dob = profile.date_of_birth ? String(profile.date_of_birth).slice(0,10) : (profile.dob ? String(profile.dob).slice(0,10) : '');
    fieldsRef.current.spouse_tin = profile.spouse_tin || profile.spouseTIN || '';
    fieldsRef.current.nid_no = profile.nid_no || profile.nid || '';
    fieldsRef.current.blood_group = profile.blood_group || '';
    fieldsRef.current.father_name = profile.father_name || '';
    fieldsRef.current.mother_name = profile.mother_name || '';
    fieldsRef.current.spouse_name = profile.spouse_name || '';
    fieldsRef.current.address = profile.address || '';
    fieldsRef.current.other_info_note = (profile.other_note || '') ;
    fieldsRef.current.file = null;

    // lists: normalize and populate refs + state (state only for structure)
    const initList = (value, mapFn, emptyTemplate) => {
      let arr = [];
      if (!value) arr = [ { ...emptyTemplate, uid: makeUid() } ];
      else {
        try {
          const parsed = typeof value === 'string' ? JSON.parse(value) : value;
          if (Array.isArray(parsed) && parsed.length) arr = parsed.map(mapFn);
          else if (parsed && typeof parsed === 'object') arr = [ mapFn(parsed) ];
          else arr = [ { ...emptyTemplate, uid: makeUid() } ];
        } catch {
          // fallback: single entry
          arr = [ { ...emptyTemplate, uid: makeUid() } ];
        }
      }
      return arr;
    };

    const accs = initList(profile.bank_details, a => ({ uid: makeUid(), account_name: a.account_name || a.bank_name || '', account_number: a.account_number || '', branch_name: a.branch_name || '', bank_name: a.bank_name || '' }), { account_name: '', account_number: '', branch_name: '', bank_name: '' });
    const asts = initList(profile.asset_details, a => ({ uid: makeUid(), name: a.name || '', purchase_date: a.purchase_date || '', amount: (a.amount != null ? String(a.amount) : '') }), { name: '', purchase_date: '', amount: '' });
    const liabs = initList(profile.liability_details, l => ({ uid: makeUid(), name: l.name || '', date: l.date || '', amount: (l.amount != null ? String(l.amount) : '') }), { name: '', date: '', amount: '' });

    const other = (() => {
      if (!profile.other_info) return [ { uid: makeUid(), key: '', value: '' } ];
      try {
        const parsed = typeof profile.other_info === 'string' ? JSON.parse(profile.other_info) : profile.other_info;
        if (Array.isArray(parsed) && parsed.length) return parsed.map(o => ({ uid: makeUid(), key: o.key ?? o.name ?? '', value: o.value ?? o.info ?? '' }));
        if (parsed && typeof parsed === 'object') return [ { uid: makeUid(), key: parsed.key || '', value: parsed.value || '' } ];
        return [ { uid: makeUid(), key: '', value: String(parsed) } ];
      } catch {
        return [ { uid: makeUid(), key: '', value: '' } ];
      }
    })();

    // populate refs for each list item
    accountsRef.current = {};
    accs.forEach(a => accountsRef.current[a.uid] = { account_name: a.account_name||'', account_number: a.account_number||'', branch_name: a.branch_name||'', bank_name: a.bank_name||'' });
    assetsRef.current = {};
    asts.forEach(a => assetsRef.current[a.uid] = { name: a.name||'', purchase_date: a.purchase_date||'', amount: a.amount||'' });
    liabilitiesRef.current = {};
    liabs.forEach(l => liabilitiesRef.current[l.uid] = { name: l.name||'', date: l.date||'', amount: l.amount||'' });
    otherRef.current = {};
    other.forEach(o => otherRef.current[o.uid] = { key: o.key||'', value: o.value||'' });

    // set structure state
    setAccounts(accs);
    setAssets(asts);
    setLiabilities(liabs);
    setOtherInfo(other);
  }, [profile?.id]);

  // add/remove helpers for lists (these change structure state)
  const addAccount = () => {
    const uid = makeUid();
    accountsRef.current[uid] = { account_name: '', account_number: '', branch_name: '', bank_name: '' };
    setAccounts(prev => [...prev, { uid, account_name: '', account_number: '', branch_name: '', bank_name: '' }]);
  };
  const removeAccount = (uid) => {
    delete accountsRef.current[uid];
    setAccounts(prev => prev.filter(a => a.uid !== uid));
  };

  const addAsset = () => {
    const uid = makeUid();
    assetsRef.current[uid] = { name: '', purchase_date: '', amount: '' };
    setAssets(prev => [...prev, { uid, name: '', purchase_date: '', amount: '' }]);
  };
  const removeAsset = (uid) => {
    delete assetsRef.current[uid];
    setAssets(prev => prev.filter(a => a.uid !== uid));
  };

  const addLiability = () => {
    const uid = makeUid();
    liabilitiesRef.current[uid] = { name: '', date: '', amount: '' };
    setLiabilities(prev => [...prev, { uid, name: '', date: '', amount: '' }]);
  };
  const removeLiability = (uid) => {
    delete liabilitiesRef.current[uid];
    setLiabilities(prev => prev.filter(l => l.uid !== uid));
  };

  const addOther = () => {
    const uid = makeUid();
    otherRef.current[uid] = { key: '', value: '' };
    setOtherInfo(prev => [...prev, { uid, key: '', value: '' }]);
  };
  const removeOther = (uid) => {
    delete otherRef.current[uid];
    setOtherInfo(prev => prev.filter(o => o.uid !== uid));
  };

  const handleFile = (e) => {
    fieldsRef.current.file = e.target.files?.[0] || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      // top-level fields from refs
      Object.entries(fieldsRef.current).forEach(([k, v]) => {
        if (k === 'file') return;
        fd.append(k, v ?? '');
      });
      // lists: build arrays from refs
      const accountsArr = Object.keys(accountsRef.current).map(uid => ({ ...accountsRef.current[uid] }));
      const assetsArr = Object.keys(assetsRef.current).map(uid => ({ ...assetsRef.current[uid], amount: Number(assetsRef.current[uid].amount) || 0 }));
      const liabilitiesArr = Object.keys(liabilitiesRef.current).map(uid => ({ ...liabilitiesRef.current[uid], amount: Number(liabilitiesRef.current[uid].amount) || 0 }));
      const otherArr = Object.keys(otherRef.current).map(uid => ({ ...otherRef.current[uid] }));

      fd.append('bank_details', JSON.stringify(accountsArr));
      fd.append('asset_details', JSON.stringify(assetsArr));
      fd.append('liability_details', JSON.stringify(liabilitiesArr));
      fd.append('other_info', JSON.stringify(otherArr));
      fd.append('date_of_birth', fieldsRef.current.dob || '');
      // include other note if needed
      if (fieldsRef.current.other_info_note) fd.append('other_note', fieldsRef.current.other_info_note);

      if (fieldsRef.current.file) fd.append('file', fieldsRef.current.file);

      // choose endpoint: /users/:id/profile when editing another user, otherwise /users/profile
      const url = targetUserId ? `/users/${targetUserId}/profile` : '/users/profile';
      // ensure multipart/form-data so backend multer receives file
      const res = await api.put(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onSaved && onSaved(res.data);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Save failed');
    }
  };

  // helper to render input with stable id and defaultValue from refs
  // modern styles
  const containerStyle = { position: 'relative', background: '#fff', padding: 28, borderRadius: 12, boxShadow: '0 10px 30px rgba(2,6,23,0.08)', maxWidth: 960, margin: '24px auto' };
  const formStyle = { display: 'grid', gap: 20 };
  const labelStyle = { display: 'block', marginBottom: 8, fontSize: 13, color: '#233240', fontWeight: 600 };
  // ensure padding is included in width and inputs can shrink to prevent overlap
  const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e6eef8', background: '#fff', boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.02)', marginBottom: 12, fontSize: 14, color: '#0f172a', boxSizing: 'border-box', minWidth: 0 };

  const TextInput = ({ id, name, defaultVal, onChangeRef, type = 'text', ...rest }) => (
    <input
      id={id}
      name={name}
      type={type}
      defaultValue={defaultVal}
      onChange={(e) => { if (onChangeRef) onChangeRef(e.target.value); }}
      style={{ ...inputStyle }}
      {...rest}
    />
  );

  return (
    <div style={containerStyle}>
      {/* Close icon top-right */}
      {onClose && (
        <button
          aria-label="Close"
          onClick={onClose}
          style={{ position: 'absolute', top: 12, right: 12, border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#334155' }}
        >
          ✕
        </button>
      )}

      <form onSubmit={handleSubmit} style={formStyle}>
        {/* Basic identity */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
          <div>
            <label htmlFor="pf-name" style={labelStyle}>Full name</label>
            <TextInput id="pf-name" name="name" defaultVal={fieldsRef.current.name} onChangeRef={(v) => fieldsRef.current.name = v} />
          </div>
          <div>
            <label htmlFor="pf-email" style={labelStyle}>Email</label>
            <TextInput id="pf-email" name="email" defaultVal={fieldsRef.current.email} onChangeRef={(v) => fieldsRef.current.email = v} type="email" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
          <div>
            <label htmlFor="pf-tin" style={labelStyle}>TIN</label>
            <TextInput id="pf-tin" name="tin" defaultVal={fieldsRef.current.tin} onChangeRef={(v) => fieldsRef.current.tin = v} />
          </div>
          <div>
            <label htmlFor="pf-nid" style={labelStyle}>NID / National ID</label>
            <TextInput id="pf-nid" name="nid_no" defaultVal={fieldsRef.current.nid_no} onChangeRef={(v) => fieldsRef.current.nid_no = v} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
          <div>
            <label htmlFor="pf-mobile" style={labelStyle}>Mobile</label>
            <TextInput id="pf-mobile" name="mobile" defaultVal={fieldsRef.current.mobile} onChangeRef={(v) => fieldsRef.current.mobile = v} />
          </div>
          <div>
            <label htmlFor="pf-registered_mobile" style={labelStyle}>Registered mobile</label>
            <TextInput id="pf-registered_mobile" name="registered_mobile" defaultVal={fieldsRef.current.registered_mobile} onChangeRef={(v) => fieldsRef.current.registered_mobile = v} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
          <div>
            <label htmlFor="pf-profession" style={labelStyle}>Profession</label>
            <TextInput id="pf-profession" name="profession" defaultVal={fieldsRef.current.profession} onChangeRef={(v) => fieldsRef.current.profession = v} />
          </div>
          <div>
            <label htmlFor="pf-dob" style={labelStyle}>Date of birth</label>
            <TextInput id="pf-dob" name="dob" type="date" defaultVal={fieldsRef.current.dob} onChangeRef={(v) => fieldsRef.current.dob = v} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <div>
            <label htmlFor="pf-blood" style={labelStyle}>Blood group</label>
            <select id="pf-blood" name="blood_group" defaultValue={fieldsRef.current.blood_group} onChange={(e) => fieldsRef.current.blood_group = e.target.value} style={{ ...inputStyle }}>
              <option value="">Select</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
          <div>
            <label htmlFor="pf-father" style={labelStyle}>Father's name</label>
            <TextInput id="pf-father" name="father_name" defaultVal={fieldsRef.current.father_name} onChangeRef={(v) => fieldsRef.current.father_name = v} />
          </div>
          <div>
            <label htmlFor="pf-mother" style={labelStyle}>Mother's name</label>
            <TextInput id="pf-mother" name="mother_name" defaultVal={fieldsRef.current.mother_name} onChangeRef={(v) => fieldsRef.current.mother_name = v} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
          <div>
            <label htmlFor="pf-spouse" style={labelStyle}>Spouse name</label>
            <TextInput id="pf-spouse" name="spouse_name" defaultVal={fieldsRef.current.spouse_name} onChangeRef={(v) => fieldsRef.current.spouse_name = v} />
          </div>
          <div>
            <label htmlFor="pf-spouse-tin" style={labelStyle}>Spouse TIN</label>
            <TextInput id="pf-spouse-tin" name="spouse_tin" defaultVal={fieldsRef.current.spouse_tin} onChangeRef={(v) => fieldsRef.current.spouse_tin = v} />
          </div>
        </div>
        <div>
          <label htmlFor="pf-address" style={labelStyle}>Address</label>
          <textarea id="pf-address" name="address" defaultValue={fieldsRef.current.address} onChange={(e) => fieldsRef.current.address = e.target.value} rows={2} style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} />
        </div>

        {/* Accounts */}
        <section style={{ padding: 12, borderRadius: 8, background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>Bank accounts</strong>
            <button type="button" onClick={addAccount} style={{ border: 'none', background: '#e6f4ff', padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>+ Add</button>
          </div>
          {accounts.map(a => (
            <div key={a.uid} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <TextInput id={`acc-name-${a.uid}`} name={`account_name_${a.uid}`} defaultVal={accountsRef.current[a.uid]?.account_name || ''} onChangeRef={(v) => accountsRef.current[a.uid].account_name = v} placeholder="Account name" />
              <TextInput id={`acc-no-${a.uid}`} name={`account_number_${a.uid}`} defaultVal={accountsRef.current[a.uid]?.account_number || ''} onChangeRef={(v) => accountsRef.current[a.uid].account_number = v} placeholder="Account number" />
              <TextInput id={`acc-branch-${a.uid}`} name={`branch_name_${a.uid}`} defaultVal={accountsRef.current[a.uid]?.branch_name || ''} onChangeRef={(v) => accountsRef.current[a.uid].branch_name = v} placeholder="Branch name" />
              <TextInput id={`acc-bank-${a.uid}`} name={`bank_name_${a.uid}`} defaultVal={accountsRef.current[a.uid]?.bank_name || ''} onChangeRef={(v) => accountsRef.current[a.uid].bank_name = v} placeholder="Bank name" />
              <button type="button" onClick={() => removeAccount(a.uid)} style={{ background: '#fdecea', color: '#d32f2f', border: 'none', padding: '6px 8px', borderRadius: 6, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </section>

        {/* Assets */}
        <section style={{ padding: 12, borderRadius: 8, background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>Assets</strong>
            <button type="button" onClick={addAsset} style={{ border: 'none', background: '#e6f4ff', padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>+ Add</button>
          </div>
          {assets.map(a => (
            <div key={a.uid} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <TextInput id={`asset-name-${a.uid}`} name={`asset_name_${a.uid}`} defaultVal={assetsRef.current[a.uid]?.name || ''} onChangeRef={(v) => assetsRef.current[a.uid].name = v} placeholder="Asset name" />
              <TextInput id={`asset-purchase-${a.uid}`} name={`asset_purchase_${a.uid}`} type="date" defaultVal={assetsRef.current[a.uid]?.purchase_date || ''} onChangeRef={(v) => assetsRef.current[a.uid].purchase_date = v} />
              <TextInput id={`asset-amount-${a.uid}`} name={`asset_amount_${a.uid}`} type="number" defaultVal={assetsRef.current[a.uid]?.amount || ''} onChangeRef={(v) => assetsRef.current[a.uid].amount = v} />
              <button type="button" onClick={() => removeAsset(a.uid)} style={{ background: '#fdecea', color: '#d32f2f', border: 'none', padding: '6px 8px', borderRadius: 6, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </section>

        {/* Liabilities */}
        <section style={{ padding: 12, borderRadius: 8, background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>Liabilities</strong>
            <button type="button" onClick={addLiability} style={{ border: 'none', background: '#e6f4ff', padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>+ Add</button>
          </div>
          {liabilities.map(l => (
            <div key={l.uid} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <TextInput id={`liab-name-${l.uid}`} name={`liability_name_${l.uid}`} defaultVal={liabilitiesRef.current[l.uid]?.name || ''} onChangeRef={(v) => liabilitiesRef.current[l.uid].name = v} placeholder="Liability name" />
              <TextInput id={`liab-date-${l.uid}`} name={`liability_date_${l.uid}`} type="date" defaultVal={liabilitiesRef.current[l.uid]?.date || ''} onChangeRef={(v) => liabilitiesRef.current[l.uid].date = v} />
              <TextInput id={`liab-amount-${l.uid}`} name={`liability_amount_${l.uid}`} type="number" defaultVal={liabilitiesRef.current[l.uid]?.amount || ''} onChangeRef={(v) => liabilitiesRef.current[l.uid].amount = v} />
              <button type="button" onClick={() => removeLiability(l.uid)} style={{ background: '#fdecea', color: '#d32f2f', border: 'none', padding: '6px 8px', borderRadius: 6, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </section>

        {/* Other info */}
        <section style={{ padding: 12, borderRadius: 8, background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>Other information</strong>
            <button type="button" onClick={addOther} style={{ border: 'none', background: '#e6f4ff', padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>+ Add</button>
          </div>
          {otherInfo.map(o => (
            <div key={o.uid} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <TextInput id={`other-key-${o.uid}`} name={`other_key_${o.uid}`} defaultVal={otherRef.current[o.uid]?.key || ''} onChangeRef={(v) => otherRef.current[o.uid].key = v} placeholder="Field name" />
              <TextInput id={`other-value-${o.uid}`} name={`other_value_${o.uid}`} defaultVal={otherRef.current[o.uid]?.value || ''} onChangeRef={(v) => otherRef.current[o.uid].value = v} placeholder="Information" />
              <button type="button" onClick={() => removeOther(o.uid)} style={{ background: '#fdecea', color: '#d32f2f', border: 'none', padding: '6px 8px', borderRadius: 6, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </section>

        {showFileInput && (
          <div>
            <label htmlFor="pf-file" style={labelStyle}>Profile picture (optional)</label>
            <input id="pf-file" type="file" accept="image/*" onChange={handleFile} style={{ marginBottom: 12 }} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
          <button type="submit" style={{ background: '#0b69ff', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: 8 }}>Save</button>
          <button type="button" onClick={() => {
            // reset to current profile values: re-run initialization effect in parent if possible
            // here we re-populate refs from profile directly
            if (profile) {
              fieldsRef.current.name = profile.name || profile.full_name || '';
              fieldsRef.current.email = profile.email || '';
              fieldsRef.current.tin = profile.tin || '';
              fieldsRef.current.mobile = profile.mobile || '';
              fieldsRef.current.registered_mobile = profile.registered_mobile || '';
              fieldsRef.current.profession = profile.profession || '';
              fieldsRef.current.dob = profile.date_of_birth ? String(profile.date_of_birth).slice(0,10) : (profile.dob ? String(profile.dob).slice(0,10) : '');
              fieldsRef.current.spouse_tin = profile.spouse_tin || profile.spouseTIN || '';
              fieldsRef.current.nid_no = profile.nid_no || profile.nid || '';
              fieldsRef.current.blood_group = profile.blood_group || '';
              fieldsRef.current.father_name = profile.father_name || '';
              fieldsRef.current.mother_name = profile.mother_name || '';
              fieldsRef.current.spouse_name = profile.spouse_name || '';
              fieldsRef.current.address = profile.address || '';
              fieldsRef.current.other_info_note = profile.other_note || '';
              // re-initialize lists by forcing the effect via parent or reload
              window.location.reload();
            } else {
              // clear
              fieldsRef.current = {
                name: '',
                email: '',
                tin: '',
                mobile: '',
                registered_mobile: '',
                profession: '',
                dob: '',
                spouse_tin: '',
                nid_no: '',
                blood_group: '',
                father_name: '',
                mother_name: '',
                spouse_name: '',
                address: '',
                other_info_note: '',
                file: null
              };
              accountsRef.current = {}; assetsRef.current = {}; liabilitiesRef.current = {}; otherRef.current = {};
              setAccounts([]); setAssets([]); setLiabilities([]); setOtherInfo([]);
            }
          }} style={{ background: '#eee', border: 'none', padding: '10px 12px', borderRadius: 8 }}>Reset</button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;